import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Home, Target, Search, BookOpen, FlaskConical,
  ClipboardList, FolderDown, MessageSquare, MessageSquareQuote,
  Repeat, Settings, LogOut, Shield,
} from 'lucide-react';

// Il logotipo ufficiale del sito, servito dal nostro dominio: prima arrivava
// dagli assets di Emergent, che non e' piu' nostro.
const LOGO_WHITE = '/logo-biounica-bianco.png';

// Il menu ha una gerarchia dichiarata: prima gli strumenti di lavoro, poi
// quello che si legge, poi le proprie cose. Le regole di visibilità vivono
// anche in server.py (PORTAL_ROUTES): se cambia il menu, vanno cambiate
// entrambe, altrimenti l'analytics conta sezioni che nessuno vede.
const blocchi = [
  {
    titolo: null,
    voci: [{ label: 'Dashboard', icon: Home, path: '/dashboard' }],
  },
  {
    titolo: 'Lavorare',
    voci: [
      { label: "Trova l'indicazione", icon: Search, path: '/trova-indicazione', editorOnly: true },
      { label: 'Aree di applicazione', icon: Target, path: '/aree-applicazione' },
      { label: 'Protocolli', icon: ClipboardList, path: '/protocolli', editorOnly: true },
      { label: 'Materiali e download', icon: FolderDown, path: '/materiali-download', editorOnly: true },
      { label: 'Racconta un caso', icon: MessageSquareQuote, path: '/racconta-un-caso' },
    ],
  },
  {
    titolo: 'Approfondire',
    voci: [
      { label: 'Conosci UMIFUL', icon: BookOpen, path: '/conosci-umiful', editorOnly: true },
      { label: 'Approfondimenti scientifici', icon: FlaskConical, path: '/approfondimenti-scientifici', editorOnly: true },
      { label: 'Feedback dei professionisti', icon: MessageSquare, path: '/feedback-professionisti' },
    ],
  },
  {
    titolo: 'Il mio account',
    voci: [
      { label: 'Riordini e provvigioni', icon: Repeat, path: '/le-mie-provvigioni', hideForMedico: true },
      { label: 'Il mio profilo', icon: Settings, path: '/profile' },
    ],
  },
];

const PROF_LABELS = {
  medico: 'Medico',
  veterinario: 'Veterinario',
  naturopata: 'Naturopata',
  operatore_olistico: 'Operatore olistico',
  erborista_parafarmacia: 'Erborista / parafarmacia',
  informatore_farmaceutico: 'Informatore farmaceutico',
  marketer_influencer: 'Marketer / influencer',
};

export default function Sidebar({ onClose }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const isMedico = user?.profile?.professional_type === 'medico';
  const isStaff = user?.is_admin || user?.is_editor || user?.role === 'admin' || user?.role === 'editor';
  const visibile = (i) => {
    if (i.hideForMedico && isMedico) return false;
    if (i.editorOnly && !isStaff) return false;
    return true;
  };
  // Un blocco senza voci visibili non lascia in giro la sua intestazione.
  const gruppi = blocchi
    .map((b) => ({ ...b, voci: b.voci.filter(visibile) }))
    .filter((b) => b.voci.length > 0);

  return (
    <div className="flex flex-col h-full w-72 bg-[#12100c] text-white" data-testid="sidebar">
      {/* Logo */}
      <div className="p-5 pb-3 border-b border-white/10">
        <img src={LOGO_WHITE} alt="Biounica" className="h-10 mb-1" data-testid="sidebar-logo" />
        <p className="font-mono text-[11px] text-white/40 tracking-[0.16em] uppercase">Portale promotori</p>
      </div>

      {/* User info */}
      <div className="px-5 py-3 border-b border-white/10">
        <p className="font-medium text-sm truncate">{user?.profile?.full_name || 'Utente'}</p>
        <p className="text-xs text-white/50 mt-0.5">
          {PROF_LABELS[user?.profile?.professional_type] || ''}
        </p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-3">
        {gruppi.map((gruppo, gi) => (
          <div key={gruppo.titolo || `gruppo-${gi}`} className="space-y-0.5">
            {gruppo.titolo && (
              <p className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-[0.16em] text-white/30 font-medium">
                {gruppo.titolo}
              </p>
            )}
            {gruppo.voci.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.label}
                  to={item.path}
                  onClick={() => { if (onClose) onClose(); }}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] transition-all duration-200
                    ${isActive ? 'bg-white/15 text-white font-medium' : 'text-white/65 hover:text-white hover:bg-white/8'}
                  `}
                  data-testid={`sidebar-link-${item.label.toLowerCase().replace(/[\s'/]/g, '-')}`}
                >
                  <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                  <span className="truncate">{item.label}</span>
                </Link>
              );
            })}
          </div>
        ))}

        {(user?.is_admin || user?.is_editor) && (
          <Link
            to="/admin"
            onClick={onClose}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] transition-all duration-200 mt-2
              ${location.pathname === '/admin'
                ? 'bg-[#c6a15b]/20 text-[#8a6620] font-medium'
                : 'text-[#8a6620]/60 hover:text-[#8a6620] hover:bg-[#c6a15b]/10'
              }`}
            data-testid="sidebar-link-admin"
          >
            <Shield className="w-4 h-4 shrink-0" strokeWidth={1.5} />
            <span>Area admin</span>
          </Link>
        )}
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-white/10">
        <button
          onClick={() => {
            logout();
            if (onClose) onClose();
          }}
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] text-white/50 hover:text-white hover:bg-white/8 transition-all w-full"
          data-testid="sidebar-logout-btn"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} />
          <span>Esci</span>
        </button>
      </div>
    </div>
  );
}
